/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Box,
  H3,
  FormControl,
  TextInput,
  Button,
  Checkbox,
} from '@ouster/webviz-react-components';

import { isNil } from 'lodash';
import { FormEvent, useEffect, useState } from 'react';

import styles from './Profile.module.css';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreState, useStoreActions } from '../../reducers';

type ProfileProps = {
  handleClose: () => void;
};

export default function Profile({ handleClose }: ProfileProps) {
  const { user, appUser, userOrgs, userOwnOrg, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );
  const {
    fetchUserOwnOrg,
    fetchUserOrgs,
    setCurUserOrg,
    updateUserName,
    updateUserMarketingPreference,
  } = useStoreActions((actions) => actions.user);
  const { createOrg } = useStoreActions((actions) => actions.organization);

  const [creatingOrg, setCreatingOrg] = useState(false);

  const { showToast } = useChakraHook();

  useEffect(() => {
    if (user?.identity_id) {
      fetchUserOwnOrg(user.identity_id);
    }
  }, [fetchUserOwnOrg, user?.identity_id]);

  const INITIAL = appUser?.name[0].toUpperCase() ?? 'U';

  async function handleNameChange(e: FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const displayName = formData.get('displayName') as string;

    try {
      if (!user) return;
      await updateUserName({
        name: displayName,
        identity_id: user?.identity_id,
      });

      showToast({
        title: 'Success',
        description: 'Your display name has been successfully updated.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to update display name',
        description: `${error}`,
        status: 'error',
      });
    }
  }

  async function handleOrgCreation(e: FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const orgName = formData.get('orgName') as string;
    try {
      setCreatingOrg(true);
      await createOrg(orgName);
      fetchUserOwnOrg(user!.identity_id);
      fetchUserOrgs(user!.identity_id);

      showToast({
        title: 'Congratulation!',
        description: 'Your organization has been successfully created.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to create organization',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setCreatingOrg(false);
    }
  }

  function switchOrganization(id: number) {
    setCurUserOrg(id);
    handleClose();
  }

  const handleMarketingPreferenceChange = async (checked: boolean) => {
    try {
      await updateUserMarketingPreference({
        identity_id: user!.identity_id,
        opt_out: !checked,
      });
      showToast({
        title: 'Success',
        description: 'Your marketing preferences have been updated.',
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to update marketing preferences',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  return (
    <div className={styles.container} data-testid="profile-root">
      <div className={styles.avatar} data-testid="profile-avatar">
        {INITIAL}
      </div>

      <div className={styles['profile-info']}>
        <Box>
          <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
            Display Name
          </H3>
          <form
            onSubmit={handleNameChange}
            data-testid="profile-displayname-form"
          >
            <FormControl paddingInline={0}>
              <div className={styles['inline-form']}>
                <TextInput
                  type="text"
                  defaultValue={appUser?.name}
                  borderRadius={4}
                  name="displayName"
                  isRequired
                />
                <Button minWidth="120px" variant="outline" type="submit">
                  Change
                </Button>
              </div>
            </FormControl>
          </form>
        </Box>

        <Box>
          <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
            Email
          </H3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '1rem',
            }}
          >
            <Box color="gray.200" data-testid="profile-email">
              {appUser?.email}
            </Box>
            {appUser && appUser.email_opt_out !== null && (
              <Checkbox
                label="Opt in to marketing communications"
                isChecked={!appUser?.email_opt_out}
                onChange={async (e) => {
                  await handleMarketingPreferenceChange(e.target.checked);
                }}
                data-testid="profile-marketing-checkbox"
              />
            )}
          </div>
        </Box>

        {isNil(userOwnOrg) ? (
          <Box>
            <H3 fontWeight={600} fontSize={16}>
              Create your own Organization
            </H3>
            <Box marginBottom="0.75em">
              <small>Each user can only create one organization</small>
            </Box>
            <form
              onSubmit={handleOrgCreation}
              data-testid="profile-create-org-form"
            >
              <FormControl paddingInline={0}>
                <div className={styles['inline-form']}>
                  <TextInput
                    type="text"
                    placeholder="Name your own organization"
                    borderRadius={4}
                    name="orgName"
                    isRequired
                  />
                  <Button
                    minWidth="120px"
                    variant="outline"
                    type="submit"
                    disabled={creatingOrg}
                  >
                    Create
                  </Button>
                </div>
              </FormControl>
            </form>
          </Box>
        ) : (
          <Box>
            <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
              Your Organization
            </H3>
            <Box
              display="flex"
              placeContent="space-between"
              alignItems="center"
            >
              {userOwnOrg.name}
              {curUserOrg?.id === userOwnOrg.id ? (
                <span>(Currently selected)</span>
              ) : (
                <Button
                  minWidth="120px"
                  variant="outline"
                  onClick={() => switchOrganization(userOwnOrg.id)}
                  data-testid="profile-switch-org-btn"
                >
                  Switch to Your Organization
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Box>
          <H3 fontWeight={600} marginBottom="0.75em" fontSize={16}>
            Joined Organizations
          </H3>
          <ul className={styles['org-list']}>
            {userOrgs.map((org) => (
              <li key={org.name}>
                {org.name} <span>({org.role.toLowerCase()})</span>
              </li>
            ))}
          </ul>
        </Box>
      </div>
    </div>
  );
}
